import { api, apiService } from '@/services/core/ApiService'
import { whileSameSession } from '@/services/core/sessionGuard'
import { useCartStore } from '@/stores/useCartStore'
import { useCouponStore } from '@/stores/useCouponStore'
import {
    ApplyCouponRequestSchema,
    CouponResponseSchema,
    CouponSuggestionsResponseSchema,
    type ICoupon,
    type ICouponResponse,
} from '~/schemas/cart/coupon'
import { cartErrorHandler } from './cartErrors'

/**
 * Cupom de desconto no carrinho (`/discountcode`). Erros com a mensagem da API no toast ("Cupom não encontrado",
 * "Erro ao aplicar o cupom. {motivo}"), menos o `cartNotFound`. O cupom do checkout é do domínio checkout.
 *
 * Gravação nas stores: o carrinho com o desconto na `useCartStore`; o cupom aplicado (`couponData`) e o escolhido
 * (`couponName`, que o checkout manda no `checkout/create`) na `useCouponStore`.
 */

function storeAppliedCoupon(response: ICouponResponse): void {
    useCartStore.getState().setCart(response.cart)
    const coupon = useCouponStore.getState()
    coupon.setCouponData(response.discountCode)
    coupon.setCouponName(response.discountCode?.code ?? response.cart.discountApplied?.discountCode ?? null)
}

function storeRemovedCoupon(response: ICouponResponse): void {
    useCartStore.getState().setCart(response.cart)
    const coupon = useCouponStore.getState()
    coupon.setCouponData(null)
    coupon.clearCouponName()
}

/** `POST discountcode/apply-coupon`: aplica o cupom no carrinho sem cupom. */
export async function applyCouponService(coupon: string): Promise<ICouponResponse> {
    return apiService(api)
        .post('discountcode/apply-coupon', () => ApplyCouponRequestSchema.parse({ coupon }), CouponResponseSchema, {
            meta: { showLoader: true },
        })
        .onSuccess(whileSameSession(storeAppliedCoupon))
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()
}

/** `PUT discountcode/update-coupon`: troca o cupom já aplicado por outro (tira o atual e aplica o novo). */
export async function updateCouponService(coupon: string): Promise<ICouponResponse> {
    return apiService(api)
        .put('discountcode/update-coupon', () => ApplyCouponRequestSchema.parse({ coupon }), CouponResponseSchema, {
            meta: { showLoader: true },
        })
        .onSuccess(whileSameSession(storeAppliedCoupon))
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()
}

/** `POST discountcode/remove-coupon`. Sem cupom aplicado responde 400 `couponError`. */
export const removeCouponService = (): Promise<ICouponResponse> =>
    apiService(api)
        .post('discountcode/remove-coupon', undefined, CouponResponseSchema, { meta: { showLoader: true } })
        .onSuccess(whileSameSession(storeRemovedCoupon))
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()

/** `GET discountcode/list-suggestion-coupons`: sugestões do modal de cupom. Sem toast: a lista só não aparece. */
export const getCouponSuggestionsService = (): Promise<ICoupon[]> =>
    apiService(api)
        .get('discountcode/list-suggestion-coupons', CouponSuggestionsResponseSchema)
        .onError(async () => {}, { useDefaultErrorHandler: false })
        .execute()
        .then((response) => response.suggestions)
